/**
 * Multi-Jurisdiction Mail-Away Component
 * 
 * Displays and manages Mail-Away requests for multiple jurisdictions per order.
 * Features:
 * - Jurisdiction list with status badges (Not Sent / Sent / Response Received)
 * - Send/Resend buttons per jurisdiction based on role
 * - Response received modal with file upload
 * - Email tracking display
 * 
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Mail, Send, RefreshCw, CheckCircle, Clock, FileText, 
  Upload, AlertCircle, RotateCcw, Eye, Building2, 
  School, MapPin, ChevronDown, ChevronUp, X, DollarSign,
  XCircle, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import API from '../config/api';
import { formatDateTimeShortET, formatDateOnly } from '../utils/formatters';
import { useDropZone } from '../hooks/useDropZone';
import { computeDelinquentGroups } from '../lib/paDelinquentGrouping';
import PaDelinquentGroupingBanner from './PaDelinquentGroupingBanner';
import MailAwayModal from './MailAwayModal';
import MailAwayPdfViewerDialog from './MailAwayPdfViewerDialog';

// SLA Configuration - Days until status becomes "Overdue"
const MAIL_AWAY_SLA_DAYS = 7; // Change to 10 if needed

// Status badge configurations - P1 UI Cleanup: Simplified labels + SLA-based escalation
const STATUS_CONFIG = {
  not_sent: {
    label: 'Not Sent',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: Clock
  },
  sent: {
    label: 'Sent',
    color: 'bg-blue-50 text-blue-600 border-blue-200',
    icon: Mail
  },
  confirmed: {
    label: 'Marked Sent',
    color: 'bg-green-50 text-green-600 border-green-200',
    icon: CheckCircle
  },
  awaiting_response: {
    label: 'Pending',
    color: 'bg-amber-50 text-amber-600 border-amber-200',
    icon: Clock
  },
  overdue: {
    label: 'Overdue',
    color: 'bg-red-50 text-red-600 border-red-200',
    icon: AlertCircle
  },
  response_received: {
    label: 'Received',
    color: 'bg-green-50 text-green-600 border-green-200',
    icon: CheckCircle
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-50 text-red-500 border-red-200',
    icon: XCircle
  }
};

/**
 * Calculate if a Mail-Away request is overdue based on SLA
 * @param {Object} request - The Mail-Away request object
 * @param {number} slaDays - Number of days before considered overdue
 * @returns {boolean} - True if overdue
 */
const isMailAwayOverdue = (request, slaDays = MAIL_AWAY_SLA_DAYS) => {
  if (!request) return false;
  
  // Only check sent/awaiting_response statuses for overdue
  const status = request.status;
  if (!['sent', 'awaiting_response'].includes(status)) return false;
  
  // Get the sent date
  const sentDate = request.sent_at || request.created_at;
  if (!sentDate) return false;
  
  // Calculate days since sent
  const sentTime = new Date(sentDate).getTime();
  const now = new Date().getTime();
  const daysSinceSent = Math.floor((now - sentTime) / (1000 * 60 * 60 * 24));
  
  return daysSinceSent >= slaDays;
};

/**
 * Get effective status considering SLA overdue logic
 * @param {Object} request - The Mail-Away request object
 * @returns {string} - Effective status key
 */
const getEffectiveStatus = (request) => {
  if (!request) return 'not_sent';
  
  const baseStatus = request.status || 'not_sent';
  
  // Check if overdue for sent/awaiting_response statuses
  if (['sent', 'awaiting_response'].includes(baseStatus) && isMailAwayOverdue(request)) {
    return 'overdue';
  }
  
  return baseStatus;
};

// Jurisdiction type icons
const JURISDICTION_ICONS = {
  County: MapPin,
  City: Building2,
  School: School,
  default: MapPin
};

/**
 * Format date for display - V1 REQUIREMENT: Use ET timezone
 */
const formatDate = formatDateTimeShortET;

/**
 * Status Badge Component - P1: Supports SLA-based overdue detection
 */
const StatusBadge = ({ status, request }) => {
  // Use effective status which considers SLA overdue logic
  const effectiveStatus = request ? getEffectiveStatus(request) : status;
  const config = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.not_sent;
  const Icon = config.icon;
  
  // Calculate days overdue for tooltip
  let daysInfo = '';
  if (effectiveStatus === 'overdue' && request) {
    const sentDate = request.sent_at || request.created_at;
    if (sentDate) {
      const daysSinceSent = Math.floor((new Date().getTime() - new Date(sentDate).getTime()) / (1000 * 60 * 60 * 24));
      daysInfo = ` (${daysSinceSent} days)`;
    }
  }
  
  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border ${config.color}`}
      title={effectiveStatus === 'overdue' ? `SLA exceeded - ${MAIL_AWAY_SLA_DAYS} day limit` : ''}
    >
      <Icon size={10} />
      {config.label}{daysInfo}
    </span>
  );
};

/**
 * Jurisdiction Row Component with inline actions
 * 
 * Button State Logic (Industry Standard):
 * 
 * Status: Action Required (sent but not confirmed)
 *   - Confirm Sent → ENABLED (Team Leader/Admin only)
 *   - Received → DISABLED
 *   - Resend → ENABLED
 * 
 * Status: Sent (confirmed)
 *   - Confirm Sent → DISABLED
 *   - Received → ENABLED
 *   - Resend → ENABLED
 * 
 * Status: Received
 *   - Confirm Sent → DISABLED
 *   - Received → DISABLED
 *   - Resend → DISABLED
 * 
 * P1: Team Leader or Admin can click "Mark Sent"
 * - Processor cannot click "Mark Sent"
 */
const JurisdictionRow = ({ 
  jurisdiction, 
  request, 
  isProcessor, 
  isTeamLeaderOrAdmin,
  hasPermission,
  orderId,
  onSend, 
  onResend, 
  onMarkSent,
  onMarkReceived,
  onCancel,
  rowLoadingAction // Per-row loading action: 'markSent' | 'resend' | 'received' | 'cancel' | null
}) => {
  const IconComponent = JURISDICTION_ICONS[jurisdiction.type] || JURISDICTION_ICONS.default;
  // Jul 2026 — MLC-PREVIEW-PERMANENT: in-app PDF viewer state.
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const status = request?.status || 'not_sent';
  const isConfirmed = !!request?.confirmed_at;
  const isReceived = status === 'response_received';
  const isCancelled = status === 'cancelled';
  
  // Calculate permissions based on status
  const hasRequest = !!request;
  
  // Button State Logic (Industry Standard):
  // "Confirm Sent" - only Team Leader/Admin, only when sent but NOT confirmed
  const canMarkSent = hasRequest && isTeamLeaderOrAdmin && 
    ['sent', 'awaiting_response'].includes(status) && !isConfirmed && !isReceived && !isCancelled;
  
  // "Resend" - only when confirmed/sent, NOT when received or cancelled
  const canResend = hasRequest && hasPermission && 
    ['sent', 'awaiting_response', 'confirmed'].includes(status) && 
    isConfirmed && !isReceived && !isCancelled;
  
  // "Received" - only after confirmed/sent, NOT when already received or cancelled
  const canMarkReceived = hasRequest && hasPermission && 
    ['sent', 'awaiting_response', 'confirmed'].includes(status) && 
    isConfirmed && !isReceived && !isCancelled;
  
  // "Cancel" - Team Leader/Admin only, only when sent/awaiting/confirmed (NOT received, NOT already cancelled)
  const canCancel = hasRequest && isTeamLeaderOrAdmin && 
    ['sent', 'awaiting_response', 'confirmed'].includes(status) && 
    !isReceived && !isCancelled;
  
  // P2 Enterprise UI: Color-based status indication (no "Not Sent" badge)
  // Blue = Action Required (sent but not confirmed)
  // Green = Confirmed/Sent or Received
  // Red = Overdue
  const effectiveStatus = request ? getEffectiveStatus(request) : status;
  
  const getStatusStyles = () => {
    if (isCancelled) return { bg: 'bg-red-50', text: 'text-red-500', label: 'Cancelled' };
    if (isReceived) return { bg: 'bg-green-50', text: 'text-green-700', label: 'Received' };
    if (effectiveStatus === 'overdue') return { bg: 'bg-red-50', text: 'text-red-700', label: 'Overdue' };
    if (isConfirmed) return { bg: 'bg-green-50', text: 'text-green-700', label: 'Sent' };
    if (hasRequest && ['sent', 'awaiting_response'].includes(status)) return { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Action Required' };
    return { bg: 'bg-gray-50', text: 'text-gray-500', label: '' }; // No badge for not_sent
  };
  
  const statusStyles = getStatusStyles();
  
  // Per-row loading indicators
  const isMarkSentLoading = rowLoadingAction === 'markSent';
  const isResendLoading = rowLoadingAction === 'resend';
  const isReceivedLoading = rowLoadingAction === 'received';
  const isCancelLoading = rowLoadingAction === 'cancel';
  const isAnyLoading = !!rowLoadingAction;
  
  return (
    <div className="flex items-center justify-between px-3 py-2.5 bg-white border border-gray-100 rounded-lg hover:bg-gray-50/50 transition-colors">
      {/* Left: Location Icon + Jurisdiction Name + Status Badge + Version Badge */}
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {/* Location Icon */}
        <div className={`p-1.5 rounded-md ${statusStyles.bg} mt-0.5 flex-shrink-0`}>
          <IconComponent size={14} className={statusStyles.text} />
        </div>

        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
        {/* Jurisdiction Name (Feb 2026 P0 — display fix).
            Current-Tax rows render the municipality name as-is
            ("Delaware County"). Delinquent rows are addressed to the
            Tax Claim Bureau, so we append a "— Tax Claim Bureau" suffix
            (or the bureau's actual name when the request carries it)
            so processors / clients see the actual recipient on the
            tracking list and audit trail. */}
        {(() => {
          const _kind = String(request?.request_kind || 'current').toLowerCase();
          if (_kind === 'delinquent') {
            const _bureauName = (
              request?.delinquent_collector_name
              || request?.payee_name
              || 'Tax Claim Bureau'
            );
            // If the bureau name already contains the jurisdiction name
            // (e.g. "Delaware County Tax Claim Bureau"), show it on its
            // own; otherwise prepend the jurisdiction so processors see
            // both the underlying jurisdiction and the bureau recipient.
            const _juris = (jurisdiction.name || '').trim();
            const _bureau = String(_bureauName).trim();
            const _label = _bureau.toLowerCase().includes(_juris.toLowerCase()) && _juris
              ? _bureau
              : (_juris ? `${_juris} — ${_bureau}` : _bureau);
            return (
              <span
                className="text-sm font-medium text-gray-800"
                data-testid={request?.request_id ? `mailaway-row-name-${request.request_id}` : undefined}
                title={`Delinquent recipient: ${_bureau}`}
              >
                {_label}
              </span>
            );
          }
          return (
            <span
              className="text-sm font-medium text-gray-800"
              data-testid={request?.request_id ? `mailaway-row-name-${request.request_id}` : undefined}
            >
              {jurisdiction.name}
            </span>
          );
        })()}
        
        {/* Status Badge */}
        {statusStyles.label && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyles.bg} ${statusStyles.text} font-medium`}>
            {statusStyles.label}
          </span>
        )}

        {/* Feb 2026 P0 — Request-kind pill. Distinguishes Current-Tax
            rows (per-municipal-collector MLC) from Delinquent (Combined)
            rows (single letter to the Tax Claim Bureau covering ≥2
            jurisdictions). Renders for every row that has a request
            record so audit / QC can see at a glance which is which. */}
        {request && (() => {
          const _kind = String(request?.request_kind || 'current').toLowerCase();
          const _covered = request?.covered_jurisdiction_ids || [];
          if (_kind === 'delinquent') {
            const _isCombined = Array.isArray(_covered) && _covered.length >= 2;
            return (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide ${
                  _isCombined
                    ? 'bg-violet-100 text-violet-800 border border-violet-200'
                    : 'bg-amber-100 text-amber-800 border border-amber-200'
                }`}
                title={
                  _isCombined
                    ? `Combined delinquent MLC covering ${_covered.length} jurisdictions`
                    : 'Single delinquent MLC for this jurisdiction'
                }
                data-testid={`mailaway-row-kind-pill-${request.request_id}`}
              >
                {_isCombined ? `Delinquent (Combined ×${_covered.length})` : 'Delinquent'}
              </span>
            );
          }
          // Phase A.7.17 Phase 2 — Current-Tax now ALSO surfaces a
          // "Combined ×N" pill when the request bundles ≥2
          // jurisdictions under one effective collector (explicit
          // collector_id OR payee fingerprint). Mirrors the
          // delinquent contract so processors / QC see the
          // bundling at a glance.
          const _isCombinedCurr = Array.isArray(_covered) && _covered.length >= 2;
          return (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide ${
                _isCombinedCurr
                  ? 'bg-teal-100 text-teal-800 border border-teal-200'
                  : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
              }`}
              title={
                _isCombinedCurr
                  ? `Combined current-tax MLC covering ${_covered.length} jurisdictions`
                  : 'Current-tax MLC addressed to the municipal collector'
              }
              data-testid={`mailaway-row-kind-pill-${request.request_id}`}
            >
              {_isCombinedCurr ? `Current-Tax (Combined ×${_covered.length})` : 'Current-Tax'}
            </span>
          );
        })()}

        {/* Version Badge - Show when version > 1 (indicates updated request) */}
        {request?.version > 1 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium border border-amber-200" title={`Request has been updated ${request.version - 1} time(s)`}>
            Updated (v{request.version})
          </span>
        )}

        {/* Cycle Badge (Feb 27, 2026) — surfaces repeat Mail-Away cycles
            for the same jurisdiction so admins can distinguish a fresh
            send from a 2nd-cycle resend driven by clarification, etc.
            Shown only for cycle ≥ 2; tooltip includes the trigger label. */}
        {request?.cycle_index >= 2 && (
          <span
            className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium border border-purple-200 cursor-help"
            title={
              request.trigger === 'post_clarification'
                ? `Cycle ${request.cycle_index} — Re-sent after Clarification`
                : request.trigger === 'post_response'
                ? `Cycle ${request.cycle_index} — Re-sent after prior response received`
                : `Cycle ${request.cycle_index} of this jurisdiction`
            }
            data-testid={`mailaway-cycle-badge-${request.request_id}`}
          >
            Cycle {request.cycle_index}
            {request.trigger === 'post_clarification' && ' · post-clarification'}
            {request.trigger === 'post_response' && ' · post-response'}
          </span>
        )}
        
        {/* Parcel Count - Show if multiple parcels */}
        {request?.parcel_numbers?.length > 1 && (
          <span className="text-xs text-gray-500" title={`Parcels: ${request.parcel_numbers.join(', ')}`}>
            ({request.parcel_numbers.length} parcels)
          </span>
        )}
        
        {/* MLC Fee Badge — MLC-FEE-PER-PARCEL (Jun 2026).
            `tax_office_fee` on the request is the authoritative
            per-request TOTAL (per-parcel base × parcels for
            current-tax; flat for delinquent) — same number the PDF
            fee table prints. `mlc_fee_per_parcel` (stamped at send
            time) drives the "($25.00×2 parcels)" breakdown label;
            legacy rows without it show the amount only (fixes the
            bogus "$25.00 ($13×2)" back-computed label). */}
        {(() => {
          const _parcelCount = (request?.parcel_numbers?.length) || 1;
          const _isCurrentKind = (request?.request_kind || 'current') === 'current';
          let _displayFee = null;
          let _perParcel = null;
          let _source = null;
          if (request?.tax_office_fee != null && !isNaN(parseFloat(request.tax_office_fee))) {
            // Authoritative stored total — matches the PDF fee table.
            _displayFee = parseFloat(request.tax_office_fee);
            if (_isCurrentKind && request?.mlc_fee_per_parcel != null && !isNaN(parseFloat(request.mlc_fee_per_parcel))) {
              _perParcel = parseFloat(request.mlc_fee_per_parcel);
            }
            _source = 'request.tax_office_fee';
          } else if (request?.collector_defaults_snapshot?.mlc_fee != null) {
            const _snap = parseFloat(request.collector_defaults_snapshot.mlc_fee);
            if (!isNaN(_snap)) {
              _perParcel = _isCurrentKind ? _snap : null;
              _displayFee = _isCurrentKind ? _snap * _parcelCount : _snap;
              _source = 'collector_defaults_snapshot.mlc_fee';
            }
          } else if (jurisdiction.mlc_charges != null) {
            const _base = parseFloat(jurisdiction.mlc_charges);
            _perParcel = _isCurrentKind ? _base : null;
            _displayFee = _isCurrentKind ? _base * _parcelCount : _base;
            _source = 'jurisdiction.mlc_charges';
          }
          if (_displayFee == null) return null;
          const _showBreakdown = _isCurrentKind && _parcelCount > 1 && _perParcel != null;
          return (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium border border-emerald-200"
              title={_showBreakdown
                ? `MLC Fee: $${_perParcel.toFixed(2)} per parcel × ${_parcelCount} parcels (source: ${_source})`
                : `MLC Fee (source: ${_source})`}
              data-testid={request?.request_id ? `mailaway-row-fee-${request.request_id}` : undefined}
            >
              ${_displayFee.toFixed(2)}
              {_showBreakdown && (
                <span className="text-emerald-500 ml-0.5 font-normal text-[10px]">
                  (${_perParcel.toFixed(2)}×{_parcelCount} parcels)
                </span>
              )}
            </span>
          );
        })()}
          </div>
          {/* Phase A.7.17 Phase 2 — Covered jurisdictions sub-line.
              Shown for combined rows (current OR delinquent) so the
              operator sees WHICH jurisdictions roll up under this
              packet. Without this line, "Combined ×3" alone leaves
              the reviewer guessing which 3. */}
          {Array.isArray(request?._covered_names) && request._covered_names.length >= 2 && (
            <div
              className="text-xs text-gray-500 mt-1 ml-0.5 truncate"
              title={`Covers: ${request._covered_names.join(', ')}`}
              data-testid={request?.request_id ? `mailaway-row-covered-${request.request_id}` : undefined}
            >
              <span className="font-semibold text-gray-600">Covers:</span>{' '}
              {request._covered_names.join(', ')}
            </div>
          )}
        </div>
      </div>
      
      {/* Right: Action Buttons - Inline with per-row loading */}
      <div className="flex items-center gap-2">
        {/* Phase A.7.17 Phase 4 — Preview PDF.
            Streams the EXACT PDF that was emailed for this Mail-Away
            request (sourced from `db.files` by `pdf_file_id` —
            never re-generated). Lets processors / QC visually
            verify the bureau letter (fee table, payee, covered
            jurisdictions) without waiting for the email.

            Jul 2026 — Popup-block fix (Methuen UAT).
            The old code called `window.open(URL.createObjectURL(blob))`
            only AFTER the fetch resolved. On Production, the backend's
            metadata-fallback path takes ~30s (Phase 18 slow-path — now
            being indexed on the BE, but until every prod pod has the
            new index we still see slow calls). By that time, browsers
            have dropped the user-gesture context that permits popups,
            so the tab silently fails to open and the operator sees
            nothing. Fix: open the tab SYNCHRONOUSLY inside the click
            handler (browsers allow it because the click is fresh),
            keep a reference to the child window, and navigate it to
            the blob URL once the fetch resolves. Also gives the user a
            visible "Preparing PDF…" placeholder page so slow loads are
            not silent. */}
        {/* Jul 2026 — MLC-PREVIEW-PERMANENT (Part C): Preview opens the
            in-app PDF viewer dialog instantly — no popup blockers, no
            blank tab, no "Preparing…" placeholder. The dialog streams
            the STORED PDF (immutable-cached by pdf_file_id); the
            "Rebuilding PDF…" state only appears on the exceptional
            self-healing path. */}
        {request?.request_id && orderId && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewOpen(true);
              }}
              disabled={isAnyLoading}
              className="text-xs h-7 px-3 text-blue-700 border-blue-200 hover:bg-blue-50"
              title="Preview the exact Mail-Away PDF that was emailed"
              data-testid={`mailaway-row-preview-pdf-${request.request_id}`}
            >
              <Eye size={12} className="mr-1" />
              Preview PDF
            </Button>
            <MailAwayPdfViewerDialog
              open={previewOpen}
              onOpenChange={setPreviewOpen}
              orderId={orderId}
              request={request}
            />
          </>
        )}
        {canMarkSent && (
          <Button
            size="sm"
            variant="default"
            onClick={(e) => { e.stopPropagation(); onMarkSent(request); }}
            disabled={isAnyLoading}
            className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-3"
          >
            {isMarkSentLoading ? (
              <>
                <RefreshCw size={12} className="mr-1 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <CheckCircle size={12} className="mr-1" />
                Confirm Sent
              </>
            )}
          </Button>
        )}
        
        {canResend && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onResend(request); }}
            disabled={isAnyLoading}
            className="text-xs h-7 px-3 border-gray-200"
          >
            {isResendLoading ? (
              <>
                <RefreshCw size={12} className="mr-1 animate-spin" />
                Resending...
              </>
            ) : (
              <>
                <RotateCcw size={12} className="mr-1" />
                Resend
              </>
            )}
          </Button>
        )}
        
        {canMarkReceived && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onMarkReceived(request); }}
            disabled={isAnyLoading}
            className="text-xs h-7 px-3 text-green-700 border-green-200 hover:bg-green-50"
          >
            {isReceivedLoading ? (
              <>
                <RefreshCw size={12} className="mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle size={12} className="mr-1" />
                Received
              </>
            )}
          </Button>
        )}
        
        {/* Show received timestamp if status is received */}
        {isReceived && request?.response_received_at && (
          <span className="text-xs text-gray-500">
            {formatDateTimeShortET(request.response_received_at)}
          </span>
        )}
        
        {/* Cancel Button — Team Leader/Admin only */}
        {canCancel && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onCancel(request); }}
            disabled={isAnyLoading}
            data-testid={`cancel-mailaway-${request?.request_id}`}
            className="text-xs h-7 px-3 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            {isCancelLoading ? (
              <>
                <RefreshCw size={12} className="mr-1 animate-spin" />
                Cancelling...
              </>
            ) : (
              <>
                <Ban size={12} className="mr-1" />
                Cancel
              </>
            )}
          </Button>
        )}
        
        {/* Show cancelled info */}
        {isCancelled && request?.cancelled_at && (
          <div className="flex flex-col items-end">
            <span className="text-xs text-red-500">
              Cancelled {formatDateTimeShortET(request.cancelled_at)}
            </span>
            {request.billing_retained && (
              <span className="text-[10px] text-amber-600 font-medium">Billing retained</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Send Mail-Away Modal
 */
const SendMailAwayModal = ({ 
  jurisdiction, 
  onConfirm, 
  onClose, 
  loading,
  // PA Delinquent Grouping (Feb 2026) — Slice 2 wiring
  orderForGrouping,         // Full order doc (for state + sibling jurisdictions)
  allJurisdictions,         // Every jurisdiction on the order (so we can detect peers)
}) => {
  const [notes, setNotes] = useState('');

  // Compute grouping plan client-side. When the order is PA AND this
  // jurisdiction has ≥1 peer (other jurisdiction on the same order)
  // pointing at the SAME delinquent collector, with the required
  // delinquent-row / same-as-current-payee gates, the banner renders.
  //
  // We pass the FULL set of jurisdictions on the order to the
  // grouping helper (not just the currently-clicked one) so the
  // shared-collector detection can find sibling rows.
  const _allIds = React.useMemo(
    () => (allJurisdictions || []).map((j) => j?.id || j?.jurisdiction_id).filter(Boolean),
    [allJurisdictions]
  );
  const _groupingPlan = React.useMemo(
    () => (orderForGrouping ? computeDelinquentGroups(orderForGrouping, _allIds) : null),
    [orderForGrouping, _allIds]
  );

  // Restrict the banner to groups that ACTUALLY include the clicked
  // jurisdiction — operator shouldn't see a banner about County+School
  // grouping when they clicked Send on an unrelated Township.
  const _relevantGroups = React.useMemo(() => {
    if (!_groupingPlan) return null;
    const jid = jurisdiction?.id || jurisdiction?.jurisdiction_id;
    if (!jid) return null;
    const rel = (_groupingPlan.groups || []).filter((g) =>
      (g.jurisdiction_ids || []).includes(String(jid))
    );
    return rel.length ? rel : null;
  }, [_groupingPlan, jurisdiction]);

  const [combineSelected, setCombineSelected] = useState(true);

  // Build the delinquent_groups payload only when banner is showing AND combine picked
  const _delinquentGroupsPayload = React.useMemo(() => {
    if (!_relevantGroups || !combineSelected) return null;
    return _relevantGroups.map((g) => ({
      delinquent_collector_id: g.delinquent_collector_id,
      jurisdiction_ids: g.jurisdiction_ids,
    }));
  }, [_relevantGroups, combineSelected]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-full">
              <Send size={20} className="text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Send Mail-Away Request
            </h3>
          </div>
          
          <p className="text-gray-600 mb-4">
            Send Mail-Away request for <strong>{jurisdiction?.name}</strong>?
          </p>

          {/* PA Delinquent-Grouping banner — only renders when the
              4-rule eligibility holds for THIS jurisdiction. */}
          {_relevantGroups && (
            <PaDelinquentGroupingBanner
              groups={_relevantGroups}
              combineSelected={combineSelected}
              onChange={setCombineSelected}
            />
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Add any notes for this request..."
              data-testid="mailaway-send-notes"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => onConfirm(jurisdiction, notes, _delinquentGroupsPayload)}
              disabled={loading}
              data-testid="mailaway-send-confirm-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Mail-Away
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              data-testid="mailaway-send-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Resend Confirmation Modal
 */
const ResendModal = ({ 
  request, 
  onConfirm, 
  onClose, 
  loading 
}) => {
  const [regeneratePdf, setRegeneratePdf] = useState(false);
  const [notes, setNotes] = useState('');
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-full">
              <RotateCcw size={20} className="text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Resend Mail-Away
            </h3>
          </div>
          
          <p className="text-gray-600 mb-4">
            Resend Mail-Away for <strong>{request?.jurisdiction_name}</strong>?
            <br />
            <span className="text-sm text-gray-500">Current version: v{request?.version || 1}</span>
          </p>
          
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={regeneratePdf}
                onChange={(e) => setRegeneratePdf(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Regenerate PDF (Correct &amp; Resend)</span>
            </label>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder="Reason for resending..."
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => onConfirm(request, regeneratePdf, notes)}
              disabled={loading}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Resending...
                </>
              ) : (
                <>
                  <RotateCcw size={16} className="mr-2" />
                  Resend (v{(request?.version || 1) + 1})
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Mark Response Received Modal
 */
const MarkReceivedModal = ({ 
  request, 
  onConfirm, 
  onClose, 
  loading 
}) => {
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  // BUG FIX (Feb 22, 2026): Unified drag-drop via shared useDropZone hook.
  // Feb 2026 — Multi-file support: up to 3 attachments per submission,
  // 30 MB total cap. Mail-Away accepts PDF/DOC/DOCX/JPG/PNG (matches
  // request contract). Backward-compatible — first file still posts as
  // `response_file=` for older backends; additional files post as
  // repeated `files=`.
  const zone = useDropZone({
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
    ],
    allowedExtensions: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    appendMode: true,
    maxFiles: 3,
    maxTotalSize: 30 * 1024 * 1024,
    onFilesAccepted: () => setError(''),
  });
  const selectedFiles = Array.isArray(zone.file) ? zone.file : [];

  const handleSubmit = () => {
    if (selectedFiles.length === 0) {
      setError('Response document is required. Please upload the response file.');
      return;
    }
    onConfirm(request, notes, selectedFiles);
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Mark Response Received
            </h3>
          </div>
          
          <p className="text-gray-600 mb-4">
            Mark response received for <strong>{request?.jurisdiction_name}</strong>?
          </p>
          
          {/* File Upload - REQUIRED (multi-file, up to 3, 30 MB total) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Response Document <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              PDF, DOC, DOCX, JPG, PNG • Maximum 10 MB per file • Up to 3 files • 30 MB total
            </p>

            {selectedFiles.length < 3 && (
              <div
                {...zone.dropzoneProps}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                  (error || zone.error) ? 'border-red-400 bg-red-50' :
                  zone.isDragging ? 'border-green-500 bg-green-50' :
                  'border-gray-300 hover:border-gray-400'
                }`}
                data-testid="mailaway-dropzone"
              >
                <div>
                  <Upload size={24} className={`mx-auto mb-2 ${(error || zone.error) ? 'text-red-400' : 'text-gray-400'}`} />
                  <p className="text-sm text-gray-500">
                    {selectedFiles.length === 0
                      ? <>Drag &amp; drop or <span className="text-blue-600 hover:text-blue-800">browse</span></>
                      : <>Add more files ({selectedFiles.length}/3 selected)</>}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, JPG, PNG</p>
                </div>
                <input {...zone.inputProps} data-testid="mailaway-file-input" />
              </div>
            )}

            {selectedFiles.length > 0 && (
              <div className={`space-y-2 ${selectedFiles.length < 3 ? 'mt-3' : ''}`} data-testid="mailaway-selected-files">
                {selectedFiles.map((f, idx) => (
                  <div
                    key={`${f.name}-${idx}-${f.size}`}
                    className="flex items-center gap-3 p-2.5 bg-green-50 border-2 border-green-200 rounded-lg"
                    data-testid={`mailaway-selected-file-${idx}`}
                  >
                    <FileText size={18} className="text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                      <p className="text-xs text-gray-500">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); zone.removeAt(idx); setError(''); }}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="Remove file"
                      data-testid={`mailaway-remove-file-${idx}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <p className="text-xs text-gray-500 text-right" data-testid="mailaway-total-size">
                  Total: {(selectedFiles.reduce((s, f) => s + (f.size || 0), 0) / 1024 / 1024).toFixed(2)} MB / 30.00 MB
                </p>
              </div>
            )}

            {(error || zone.error) && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle size={14} />
                {error || zone.error}
              </p>
            )}
          </div>
          
          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              rows={3}
              placeholder="Notes about the response..."
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  Mark Received
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Cancel Mail-Away Modal
 * Two variants: Pre-Send (no billing) vs Post-Send (billing retained)
 */
const CancelMailAwayModal = ({ 
  request, 
  onConfirm, 
  onClose, 
  loading 
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  
  const isPostSend = !!request?.confirmed_at || request?.status === 'confirmed';
  
  const handleSubmit = () => {
    if (isPostSend && (!reason.trim() || reason.trim().length < 5)) {
      setError('A cancellation reason (minimum 5 characters) is required for confirmed requests.');
      return;
    }
    setError('');
    onConfirm(request, reason.trim() || null);
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-full ${isPostSend ? 'bg-amber-100' : 'bg-red-100'}`}>
              <Ban size={20} className={isPostSend ? 'text-amber-600' : 'text-red-600'} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Cancel Mail-Away
            </h3>
          </div>
          
          {/* Context-aware message */}
          {isPostSend ? (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium mb-1">
                This Mail-Away has already been confirmed as sent.
              </p>
              <p className="text-xs text-amber-700">
                Cancelling will stop further processing, but the request will remain visible for billing and audit.
              </p>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 font-medium mb-1">
                This Mail-Away has not been confirmed as sent.
              </p>
              <p className="text-xs text-blue-700">
                It will be cancelled without any billing impact.
              </p>
            </div>
          )}
          
          <p className="text-gray-600 mb-4 text-sm">
            Cancel Mail-Away for <strong>{request?.jurisdiction_name}</strong>?
          </p>
          
          {/* Reason field — required for post-send, optional for pre-send */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cancellation Reason {isPostSend && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError(''); }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                error ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'
              }`}
              rows={3}
              placeholder={isPostSend ? 'Reason for cancellation (required)...' : 'Reason for cancellation (optional)...'}
              data-testid="cancel-reason-input"
            />
            {error && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle size={14} />
                {error}
              </p>
            )}
          </div>
          
          {/* Summary of what will happen */}
          <div className="mb-4 text-xs text-gray-500 space-y-1">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${isPostSend ? 'bg-amber-500' : 'bg-gray-400'}`} />
              <span>Billing retained: <strong>{isPostSend ? 'Yes' : 'No'}</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${isPostSend ? 'bg-amber-500' : 'bg-gray-400'}`} />
              <span>Billing review required: <strong>{isPostSend ? 'Yes' : 'No'}</strong></span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="confirm-cancel-mailaway-btn"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <Ban size={16} className="mr-2" />
                  Cancel Mail-Away
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Cycle-Acknowledgment Modal (Feb 27, 2026 — Resume support added Jun 2026)
 *
 * Shown when the backend returns 409 MAILAWAY_CYCLE_ACK_REQUIRED for
 * a Mail-Away send targeting a jurisdiction that already has prior
 * history. Two variants:
 *
 *   (a) Resume variant — green confirmation. Backend marked the
 *       latest pending cycle as `resume_eligible: true` (clarification
 *       round-trip, identical shipment signature). NO new charges.
 *
 *   (b) Cycle N variant — original yellow warning. New physical
 *       mailing, new MLC + Shipping charges.
 *
 * Resume is system-detected (not a separate menu item) so the Processor
 * never has to remember which button to click.
 */
const CycleAckModal = ({ jurisdiction, notes, pendingCycles, loading, onConfirm, onClose }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const cycle = pendingCycles[0] || {};
  const newCycleIndex = cycle.cycle_index || 2;
  const isResumeEligible = !!cycle.resume_eligible;
  const prevSent = cycle.previous_sent_at
    ? formatDateOnly(cycle.previous_sent_at)
    : '—';
  const prevMlc = Number(cycle.previous_mlc_fee || 0).toFixed(2);
  const prevShipping = Number(cycle.previous_shipping_fee || 0).toFixed(2);
  const newTotal = (Number(cycle.previous_mlc_fee || 0) + Number(cycle.previous_shipping_fee || 0)).toFixed(2);

  const triggerLabel = {
    post_clarification: 'Re-sent after Clarification',
    post_response: 'Re-sent after prior response received',
    manual_resend: 'Manual repeat send',
  }[cycle.trigger] || 'New cycle';

  // ───────── Resume variant ─────────
  if (isResumeEligible) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
        data-testid="mailaway-resume-modal"
      >
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
          <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-200 flex items-start gap-3">
            <div className="text-2xl leading-none">✓</div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-emerald-900">
                Resuming Existing Mail-Away
              </h3>
              <p className="text-xs text-emerald-800 mt-1">
                Order returned from Clarification with the same payee, address,
                phone, shipping method, and parcel list. This will <strong>not</strong> generate
                a new MLC or Shipping charge.
              </p>
            </div>
          </div>

          <div className="px-5 py-4 space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Jurisdiction</span>
                <span className="font-medium text-gray-900">{cycle.jurisdiction_name || jurisdiction?.name}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Original sent</span>
                <span className="font-medium text-gray-900" data-testid="mailaway-resume-prev-sent">
                  {prevSent}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Original cycle</span>
                <span className="font-medium text-gray-900">Cycle {newCycleIndex - 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Returned from</span>
                <span className="font-medium text-emerald-700">Clarification</span>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-emerald-900 mb-1">Billing</div>
              <div className="text-emerald-800">
                Resumed existing Mail-Away — <strong>no new charge.</strong>
              </div>
              <div className="text-xs text-emerald-700 mt-2">
                Cycle 1 charges (MLC ${prevMlc} + Shipping ${prevShipping}) remain unchanged.
              </div>
            </div>
          </div>

          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              data-testid="mailaway-resume-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => onConfirm({ resume: true })}
              disabled={loading}
              data-testid="mailaway-resume-confirm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {loading ? 'Resuming…' : 'Continue with Existing Mailing'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ───────── Legacy Cycle N variant (unchanged) ─────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="mailaway-cycle-ack-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="px-5 py-4 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
          <div className="text-2xl leading-none">⚠️</div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-amber-900">
              New Mail-Away Cycle — Confirm Required
            </h3>
            <p className="text-xs text-amber-800 mt-1">
              This jurisdiction already has prior Mail-Away history.
              Proceeding will create <strong>Cycle {newCycleIndex}</strong> with
              new MLC + Shipping charges.
            </p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">Jurisdiction</span>
              <span className="font-medium text-gray-900">{cycle.jurisdiction_name || jurisdiction?.name}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">Previous cycle sent</span>
              <span className="font-medium text-gray-900">{prevSent}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">Previous status</span>
              <span className="font-medium text-gray-900 capitalize">{(cycle.previous_status || '').replace(/_/g, ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">This will be</span>
              <span className="font-medium text-purple-700">Cycle {newCycleIndex} — {triggerLabel}</span>
            </div>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
            <div className="font-medium text-rose-900 mb-2">New charges that will be added</div>
            <div className="flex justify-between text-rose-800">
              <span>MLC Fee</span>
              <span>${prevMlc}</span>
            </div>
            <div className="flex justify-between text-rose-800">
              <span>Shipping</span>
              <span>${prevShipping}</span>
            </div>
            <div className="flex justify-between text-rose-900 font-semibold pt-1 mt-1 border-t border-rose-200">
              <span>Cycle {newCycleIndex} total</span>
              <span>${newTotal}</span>
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="mailaway-cycle-ack-checkbox"
            />
            <span className="text-sm text-gray-700">
              I confirm this is a <strong>new physical mailing</strong> and
              acknowledge that a new MLC + Shipping charge will be billed.
            </span>
          </label>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            data-testid="mailaway-cycle-ack-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm({ resume: false })}
            disabled={!acknowledged || loading}
            data-testid="mailaway-cycle-ack-confirm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {loading ? 'Sending…' : `Send Cycle ${newCycleIndex}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export { CycleAckModal };

/**
 * Main Multi-Jurisdiction Mail-Away Component
 */
// MLS-MAILAWAY-ENTITY-001: `extraRows` (MLS rows, rendered before the Total) + `extraSummary` {total,pending,received,cancelled,fee,feeCount}
// let Tax + MLS requests coexist in ONE Order Summary section with combined counts / Total MLC Fees.
const MultiJurisdictionMailAway = ({ orderId, orderData, currentUser, onUpdate, extraRows = null, extraSummary = null }) => {
  const [mailAwayData, setMailAwayData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-row loading state: { [request_id]: 'markSent' | 'resend' | 'received' | null }
  const [rowLoadingState, setRowLoadingState] = useState({});
  const [error, setError] = useState(null);
  
  // Modal states
  const [sendModal, setSendModal] = useState({ open: false, jurisdiction: null });
  const [resendModal, setResendModal] = useState({ open: false, request: null });
  const [receiveModal, setReceiveModal] = useState({ open: false, request: null });
  const [cancelModal, setCancelModal] = useState({ open: false, request: null });
  // Soft duplicate-cycle ack modal (Feb 27, 2026)
  // Opens when /mailaway/send returns 409 MAILAWAY_CYCLE_ACK_REQUIRED.
  // The user reviews prior-cycle context and must tick the
  // confirmation checkbox before we retry the same send with
  // cycle_acknowledged: true.
  const [cycleAckModal, setCycleAckModal] = useState({
    open: false,
    jurisdiction: null,
    notes: null,
    pendingCycles: [],
  });

  // Feb 2026 — Option C: order-level "Send Mail-Away for All" modal
  // state. Owns its own MailAwayModal instance so the button-driven
  // batch flow lives entirely inside the Mail-Away tab.
  const [sendForAllOpen, setSendForAllOpen] = useState(false);
  const [orderForBatch, setOrderForBatch] = useState(null);
  const [certForBatch, setCertForBatch] = useState(null);
  
  // Helper to set loading for a specific row
  const setRowLoading = (requestId, action) => {
    setRowLoadingState(prev => ({ ...prev, [requestId]: action }));
  };
  
  // Helper to clear loading for a specific row
  const clearRowLoading = (requestId) => {
    setRowLoadingState(prev => ({ ...prev, [requestId]: null }));
  };
  
  // User role checks
  const isProcessor = (() => {
    const r = (currentUser?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return ['processor', 'tax_processor', 'title_search_processor', 'search_processor'].includes(r);
  })();
  const hasPermission = currentUser?.role === 'admin' || 
                        currentUser?.role === 'tax_search_team_leader' || 
                        currentUser?.role === 'title_search_team_leader';
  // P1: Team Leader or Admin can click "Mark Sent"
  const isTeamLeaderOrAdmin = currentUser?.role === 'admin' || 
                              currentUser?.role === 'tax_search_team_leader' || 
                              currentUser?.role === 'title_search_team_leader';
  
  // Fetch Mail-Away data
  const fetchMailAwayData = useCallback(async () => {
    if (!orderId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/orders/${orderId}/mailaway/requests`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setMailAwayData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching Mail-Away data:', err);
      setError('Failed to load Mail-Away data');
    } finally {
      setLoading(false);
    }
  }, [orderId]);
  
  useEffect(() => {
    fetchMailAwayData();
  }, [fetchMailAwayData]);
  
  // Send Mail-Away
  // `cycleAcknowledged` is the legacy "I confirm new mailing" boolean.
  // `resume` (Jun 2026) means: take the Resume path for THIS jurisdiction —
  // no new MLC/Shipping charges, just resume the existing Cycle N-1 mailing
  // that was paused while waiting on Clarification.
  const handleSend = async (jurisdiction, notes, cycleAcknowledged = false, resume = false, delinquentGroups = null) => {
    const tempId = jurisdiction.id; // Use jurisdiction ID for new sends
    setRowLoading(tempId, 'send');
    try {
      const body = {
        jurisdiction_ids: [jurisdiction.id],
        notes: notes || null,
        cycle_acknowledged: cycleAcknowledged
      };
      if (resume) {
        body.resume_jurisdiction_ids = [jurisdiction.id];
        // Send the order's current status so the server's resume gate can
        // confirm we're returning from Clarification even if its internal
        // trigger heuristic missed the note.
        body.order_previous_status =
          orderData?.previous_status ||
          orderData?.status ||
          'clarification';
      }
      // Feb 2026 — PA Delinquent-Collector Grouping (Slice 2).
      // When the operator picks "Combine" in the in-modal banner,
      // the SendMailAwayModal forwards a `delinquent_groups` array
      // here. We thread it onto the payload only when non-empty so
      // the legacy 1-per-jurisdiction path is bit-for-bit unchanged
      // when no grouping applies.
      if (Array.isArray(delinquentGroups) && delinquentGroups.length > 0) {
        body.delinquent_groups = delinquentGroups;
      }
      const response = await axios.post(
        `${API}/api/orders/${orderId}/mailaway/send`,
        body,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        const result = response.data.results[0];
        if (result?.resumed) {
          // Jun 2026 — resume path; no new charges generated.
          toast.success('Mail-Away resumed', {
            description: 'Resumed existing Mail-Away — no new charge.',
            duration: 4000,
          });
        } else if (result.email_sent) {
          toast.success('Mail-Away sent successfully', {
            description: `Email sent to ${result.email_recipient}`,
            duration: 4000
          });
        } else {
          // Jun 2026 — Phase 15 (UAT-MAILAWAY-INITIAL-SEND-FAITHFUL).
          // The dispatch closure now stamps the structured error
          // code on each result row. Show the EXACT backend error
          // (NO_RECIPIENT, SES_NOT_INITIALIZED, PDF_MISSING,
          // EXCEPTION, etc.) so Ops sees what to fix — no more
          // hardcoded misleading copy that masked the real cause.
          const _err = result.email_error || 'Email not sent — check the activity timeline.';
          const _code = result.email_error_code ? ` [${result.email_error_code}]` : '';
          toast.error('Mail-Away created — email NOT sent', {
            description: `${jurisdiction.name}: ${_err}${_code}`,
            duration: 10000
          });
        }
        setSendModal({ open: false, jurisdiction: null });
        setCycleAckModal({ open: false, jurisdiction: null, notes: null, pendingCycles: [] });
        // Inline refresh - NO navigation
        await fetchMailAwayData();
        if (onUpdate) onUpdate();
      } else {
        // Per-jurisdiction result blocked. Route based on the structured
        // `suggested_action` returned by the backend so the user sees
        // the correct modal/toast instead of a misleading "Use Resend".
        const result = response.data.results?.[0] || {};
        const suggested = result.suggested_action;
        const refreshAndClose = async () => {
          // Always refresh the row so the right inline button (Resend /
          // Confirm Sent) becomes available immediately.
          await fetchMailAwayData();
          if (onUpdate) onUpdate();
        };

        if (suggested === 'resume') {
          // Open the green Resume modal via the same Cycle Ack modal
          // payload shape — it already renders the Resume branch when
          // `resume_eligible: true` on the pending cycle row.
          setSendModal({ open: false, jurisdiction: null });
          setCycleAckModal({
            open: true,
            jurisdiction,
            notes,
            pendingCycles: [{
              jurisdiction_id: result.jurisdiction_id,
              jurisdiction_name: result.jurisdiction_name || jurisdiction.name,
              cycle_index: 2,
              trigger: 'post_clarification',
              previous_request_id: result.previous_request_id,
              previous_status: result.previous_request_status,
              resume_eligible: true,
              resume_reason: 'match_after_clarification',
              resume_diff_fields: [],
            }],
          });
          return;
        }

        if (suggested === 'resend') {
          // Match the existing request row and open the Resend modal directly.
          const matchedReq = (mailAwayData?.requests || []).find(
            (r) => r.request_id === result.previous_request_id
          );
          setSendModal({ open: false, jurisdiction: null });
          if (matchedReq) {
            setResendModal({ open: true, request: matchedReq });
          } else {
            toast.info('Mail-Away already sent — use Resend', {
              description: result.error,
              duration: 6000,
            });
            await refreshAndClose();
          }
          return;
        }

        if (suggested === 'confirm_sent') {
          // The TL/Admin must confirm the prior send before resend works.
          toast.warning('Confirm Sent required', {
            description: result.error,
            duration: 6000,
          });
          setSendModal({ open: false, jurisdiction: null });
          await refreshAndClose();
          return;
        }

        if (suggested === 'cycle2') {
          // Surface the yellow Cycle 2 confirmation modal — same path the
          // global cycle-ack 409 takes, but synthesized from result data.
          setSendModal({ open: false, jurisdiction: null });
          setCycleAckModal({
            open: true,
            jurisdiction,
            notes,
            pendingCycles: [{
              jurisdiction_id: result.jurisdiction_id,
              jurisdiction_name: result.jurisdiction_name || jurisdiction.name,
              cycle_index: 2,
              trigger: 'manual_resend',
              previous_request_id: result.previous_request_id,
              previous_status: result.previous_request_status,
              resume_eligible: false,
              resume_reason: 'shipment_signature_changed',
              resume_diff_fields: result.resume_diff_fields || [],
            }],
          });
          return;
        }

        toast.error('Failed to send Mail-Away', {
          description: result.error || 'Unknown error',
          duration: 5000,
        });
      }
    } catch (err) {
      // 409 = backend wants explicit ack for a repeat cycle.
      // Open the dedicated duplicate-warning modal instead of toasting.
      const detail = err.response?.data?.detail;
      if (
        err.response?.status === 409 &&
        detail?.error === 'MAILAWAY_CYCLE_ACK_REQUIRED'
      ) {
        setSendModal({ open: false, jurisdiction: null });
        setCycleAckModal({
          open: true,
          jurisdiction,
          notes,
          pendingCycles: detail.pending_cycles || [],
        });
        return;
      }
      console.error('Error sending Mail-Away:', err);
      toast.error('Failed to send Mail-Away', {
        description: err.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      clearRowLoading(tempId);
    }
  };
  
  // Resend Mail-Away
  const handleResend = async (request, regeneratePdf, notes) => {
    setRowLoading(request.request_id, 'resend');
    try {
      const params = new URLSearchParams();
      if (regeneratePdf) params.append('regenerate_pdf', 'true');
      if (notes) params.append('notes', notes);
      
      const response = await axios.post(
        `${API}/api/orders/${orderId}/mailaway/requests/${request.request_id}/resend?${params.toString()}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Jun 2026 — UAT-MAILAWAY-RESEND-FAITHFUL-TIMELINE
      // The backend now returns `success: false` when the email
      // service refused (PDF_MISSING) or raised — the row stays
      // in pending_send and the activity timeline records a
      // "Mail-Away resend FAILED" event, NOT "Mail-Away resent".
      // Mirror that on the UI: success toast ONLY when the email
      // actually went out, red toast with the exact backend error
      // otherwise. Never show "Version N sent" unless email_sent
      // is true.
      if (response.data && response.data.success === true && response.data.email_sent === true) {
        toast.success('Mail-Away resent', {
          description: `Version ${response.data.version} sent for ${request.jurisdiction_name}`,
          duration: 4000
        });
        setResendModal({ open: false, request: null });
        await fetchMailAwayData();
        if (onUpdate) onUpdate();
      } else {
        // Backend kept the row in pending_send and wrote a
        // resend_failed activity. Phase 14 — when a structured
        // regen failure reason is present, render the SPECIFIC
        // data-level detail (e.g. "Jurisdiction not found — was
        // it deactivated?") so Ops sees exactly what to fix.
        const _regenFail = response.data && response.data.pdf_regen_failure;
        let _msg;
        if (_regenFail && _regenFail.detail) {
          _msg = `${_regenFail.detail} [code: ${_regenFail.code || 'UNKNOWN'}]`;
        } else {
          _msg = (response.data && (response.data.error || response.data.message))
            || 'Email could not be sent — request remains in pending_send. Check the activity timeline for details.';
        }
        toast.error('Resend failed — email NOT delivered', {
          description: _msg,
          duration: 10000  // give Ops time to read the specific fix message
        });
        // Keep the modal open so Ops can edit notes / retry, but
        // refresh the dashboard so the row's new pending_send
        // status and error fields surface immediately.
        await fetchMailAwayData();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error resending Mail-Away:', err);
      toast.error('Failed to resend Mail-Away', {
        description: err.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      clearRowLoading(request.request_id);
    }
  };
  
  // P1: Mark Sent (Confirmation only - NO email sent)
  // Only Team Leader/Admin can click this
  const handleMarkSent = async (request) => {
    if (!isTeamLeaderOrAdmin) {
      toast.error('Permission denied', {
        description: 'Only Team Leaders and Admins can confirm Mail-Away',
        duration: 4000
      });
      return;
    }
    
    setRowLoading(request.request_id, 'markSent');
    try {
      const response = await axios.post(
        `${API}/api/orders/${orderId}/mailaway/mark-sent`,
        {
          request_id: request.request_id
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        toast.success('Mail-Away marked as sent', {
          description: `Confirmed for ${request.jurisdiction_name}`,
          duration: 4000
        });
        // Inline refresh - NO navigation, NO modal close, NO redirect
        await fetchMailAwayData();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error marking Mail-Away as sent:', err);
      toast.error('Failed to mark as sent', {
        description: err.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      clearRowLoading(request.request_id);
    }
  };
  
  // Mark Response Received
  // Feb 2026 — Multi-file: `files` is an array (length 1-3, total ≤ 30 MB).
  // Backward-compat: first file still posts as `response_file=` so older
  // backends keep working. Additional files post as repeated `files=`.
  const handleMarkReceived = async (request, notes, filesArg) => {
    setRowLoading(request.request_id, 'received');
    try {
      const files = Array.isArray(filesArg) ? filesArg : (filesArg ? [filesArg] : []);
      const formData = new FormData();
      if (notes) formData.append('notes', notes);
      if (files.length > 0) {
        formData.append('response_file', files[0]);
        for (const f of files) {
          formData.append('files', f);
        }
      }
      
      const response = await axios.post(
        `${API}/api/orders/${orderId}/mailaway/requests/${request.request_id}/response`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        // Check if order status changed to mail_away_received (all responses received)
        const allReceived = response.data.all_responses_received;
        const newStatus = response.data.order_status;
        
        if (allReceived && newStatus === 'mail_away_received') {
          toast.success('All responses received!', {
            description: `${request.jurisdiction_name} - Order status updated to "Mail-Away Received"`,
            duration: 5000
          });
        } else {
          toast.success('Response received', {
            description: request.jurisdiction_name,
            duration: 4000
          });
        }
        
        setReceiveModal({ open: false, request: null });
        // Inline refresh - NO navigation
        await fetchMailAwayData();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error marking response received:', err);
      toast.error('Failed to mark response received', {
        description: err.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      clearRowLoading(request.request_id);
    }
  };
  
  // Cancel Mail-Away Request
  const handleCancel = async (request, reason) => {
    setRowLoading(request.request_id, 'cancel');
    try {
      const response = await axios.post(
        `${API}/api/orders/${orderId}/mailaway/requests/${request.request_id}/cancel`,
        { reason: reason || null },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        const cancelType = response.data.cancel_type;
        const billingRetained = response.data.billing_retained;
        
        if (cancelType === 'post-send') {
          toast.success('Mail-Away cancelled (post-send)', {
            description: `${request.jurisdiction_name} — billing retained for audit`,
            duration: 5000
          });
        } else {
          toast.success('Mail-Away cancelled', {
            description: `${request.jurisdiction_name} — no billing impact`,
            duration: 4000
          });
        }
        
        setCancelModal({ open: false, request: null });
        // Inline refresh
        await fetchMailAwayData();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error cancelling Mail-Away:', err);
      toast.error('Failed to cancel Mail-Away', {
        description: err.response?.data?.detail || 'Please try again',
        duration: 5000
      });
    } finally {
      clearRowLoading(request.request_id);
    }
  };
  
  // Build jurisdiction list with request data
  // Include both available jurisdictions AND any existing requests that may not be in available list
  // For jurisdictions with multiple cycles (Feb 27, 2026), surface the
  // MOST RECENT request so the cycle badge reflects the active cycle.
  //
  // Feb 2026 P0 — also surface combined-delinquent rows independently
  // of their primary jurisdiction's current-tax row. A jurisdiction can
  // legitimately have BOTH a current-tax request AND a combined-
  // delinquent request that happens to use its id as the primary key;
  // both must be visible to the operator with their respective kind
  // pills. We group requests by (jurisdiction_id, request_kind) instead
  // of (jurisdiction_id) alone.
  const _hdr = {
    total: (mailAwayData?.total_requests || 0) + (extraSummary?.total || 0),
    pending: (mailAwayData?.pending_count || 0) + (extraSummary?.pending || 0),
    received: (mailAwayData?.received_count || 0) + (extraSummary?.received || 0),
    cancelled: (mailAwayData?.cancelled_count || 0) + (extraSummary?.cancelled || 0),
  };
  const _kindOf = (r) => String(r?.request_kind || 'current').toLowerCase();
  // Phase A.7.17 Phase 2 — when a request is a Combined Current-Tax
  // MLC (covered_jurisdiction_ids ≥ 2) the persisted `jurisdiction_id`
  // is the "primary" anchor (an arbitrary covered member) but the
  // persisted `jurisdiction_name` carries the LEAD collector name
  // (e.g. "WEST BRUNSWICK TOWNSHIP"). Without this override, the row
  // would display the primary covered member's name (e.g. "BLUE
  // MOUNTAIN SCHOOL DISTRICT") because the matching available
  // jurisdiction's `j.name` won out over `request.jurisdiction_name`.
  // Mirrors the delinquent path which already pulls
  // `delinquent_collector_name` from the request payload.
  const _isCombinedCurrent = (r) =>
    _kindOf(r) === 'current'
    && Array.isArray(r?.covered_jurisdiction_ids)
    && r.covered_jurisdiction_ids.length >= 2;

  // Build id → name lookup so combined rows can render
  // "Covers: A, B, C" below the row title.
  const _juris_name_by_id = (mailAwayData?.available_jurisdictions || []).reduce(
    (acc, j) => {
      if (j?.id) acc[String(j.id)] = j.name || j.id;
      return acc;
    },
    {},
  );
  // Also fall back to any request that anchors on a covered id (it
  // carries the original municipality name even when the row is
  // surfacing the lead). Mostly redundant with available_jurisdictions
  // but defensive — a request can persist after its jurisdiction was
  // removed from the cert.
  for (const _r of (mailAwayData?.requests || [])) {
    const _rid = _r?.jurisdiction_id;
    if (_rid && !_juris_name_by_id[String(_rid)] && _kindOf(_r) !== 'current') {
      // Skip current-tax requests here — their `jurisdiction_name` is
      // the collector, not the municipality, and would mislabel a
      // covered member.
      _juris_name_by_id[String(_rid)] = _r.jurisdiction_name || _rid;
    }
  }
  const _coveredNames = (r) => {
    const _ids = Array.isArray(r?.covered_jurisdiction_ids)
      ? r.covered_jurisdiction_ids
      : [];
    return _ids
      .map((id) => _juris_name_by_id[String(id)] || id)
      .filter(Boolean);
  };
  const _decorate = (r) => {
    if (!r) return r;
    const _covers = _coveredNames(r);
    return _covers.length >= 2 ? { ...r, _covered_names: _covers } : r;
  };

  const availableJurisdictionList = (mailAwayData?.available_jurisdictions || []).flatMap(j => {
    const matching = (mailAwayData?.requests || []).filter(r => r.jurisdiction_id === j.id);
    if (matching.length === 0) return [{ jurisdiction: j, request: undefined }];
    // Bucket by kind so current-tax and delinquent both render.
    const byKind = matching.reduce((acc, r) => {
      const k = _kindOf(r);
      (acc[k] = acc[k] || []).push(r);
      return acc;
    }, {});
    return Object.entries(byKind).map(([_kind, reqs]) => {
      let request;
      if (reqs.length === 1) {
        request = reqs[0];
      } else {
        request = [...reqs].sort((a, b) => {
          const ca = a.cycle_index || 0;
          const cb = b.cycle_index || 0;
          if (cb !== ca) return cb - ca;
          const ta = a.sent_at || a.created_at || '';
          const tb = b.sent_at || b.created_at || '';
          return String(tb).localeCompare(String(ta));
        })[0];
      }
      // Combined Current-Tax — override the row's display
      // jurisdiction so the name reflects the lead collector. We
      // ONLY override `name` (keep `id` + `type` for keying /
      // permissions so existing flows aren't disturbed).
      const _jurForRow = _isCombinedCurrent(request)
        ? { ...j, name: request.jurisdiction_name || j.name }
        : j;
      return { jurisdiction: _jurForRow, request: _decorate(request) };
    });
  });

  // Add any requests that are NOT in available jurisdictions (edge case: manually added requests)
  const existingRequestsNotInAvailable = (mailAwayData?.requests || [])
    .filter(r => !availableJurisdictionList.some(item =>
      item.request
        && item.request.request_id === r.request_id
    ))
    .filter(r => !(mailAwayData?.available_jurisdictions || []).some(j => j.id === r.jurisdiction_id && _kindOf(r) === 'current'))
    .map(r => ({
      jurisdiction: {
        id: r.jurisdiction_id,
        name: r.jurisdiction_name,
        type: r.jurisdiction_type || 'Unknown',
        parcel_number: 'N/A'
      },
      request: _decorate(r)
    }));
  
  // Combine: available jurisdictions first, then any additional requests
  const jurisdictionList = [...availableJurisdictionList, ...existingRequestsNotInAvailable];
  
  if (loading) {
    return (
      <div className="p-4 text-center">
        <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
        <p className="text-sm text-gray-500">Loading Mail-Away data...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-center text-red-600">
        <AlertCircle className="h-6 w-6 mx-auto mb-2" />
        <p className="text-sm">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchMailAwayData} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }
  
  if (!mailAwayData || jurisdictionList.length === 0) {
    // Check if there are already mail-away requests (show them even if no "available" jurisdictions)
    const hasExistingRequests = mailAwayData?.requests?.length > 0;
    
    if (hasExistingRequests) {
      // Show existing requests even if no new jurisdictions available
      const existingRequestsList = mailAwayData.requests.map(request => ({
        jurisdiction: {
          id: request.jurisdiction_id,
          name: _isCombinedCurrent(request)
            ? (request.jurisdiction_name || 'Combined Current-Tax')
            : request.jurisdiction_name,
          type: request.jurisdiction_type,
          parcel_number: 'N/A'
        },
        request: _decorate(request)
      }));
      
      return (
        <div className="space-y-3">
          {/* Header with Summary - Compact */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Mail-Away Requests</h3>
              <p className="text-xs text-gray-500">
                {_hdr.total} {_hdr.total === 1 ? 'request' : 'requests'} • {_hdr.pending} pending • {_hdr.received} received{_hdr.cancelled > 0 ? ` • ${_hdr.cancelled} cancelled` : ''}
              </p>
            </div>
          </div>
          
          {/* List of existing requests - Directly below header */}
          <div className="space-y-2">
            {existingRequestsList.map(({ jurisdiction, request }) => (
              <JurisdictionRow
                key={request.request_id}
                jurisdiction={jurisdiction}
                request={request}
                hasPermission={hasPermission}
                isProcessor={isProcessor}
                isTeamLeaderOrAdmin={isTeamLeaderOrAdmin}
                orderId={orderId}
                rowLoadingAction={rowLoadingState[request.request_id]}
                onSend={() => {}}
                onResend={() => setResendModal({ open: true, request })}
                onMarkSent={() => handleMarkSent(request)}
                onMarkReceived={() => setReceiveModal({ open: true, request })}
                onCancel={(r) => setCancelModal({ open: true, request: r })}
              />
            ))}
          </div>
          
          {/* Modals */}
          {resendModal.open && resendModal.request && (
            <ResendModal
              request={resendModal.request}
              onClose={() => setResendModal({ open: false, request: null })}
              onConfirm={handleResend}
              loading={rowLoadingState[resendModal.request?.request_id] === 'resend'}
            />
          )}
          
          {receiveModal.open && receiveModal.request && (
            <MarkReceivedModal
              request={receiveModal.request}
              onClose={() => setReceiveModal({ open: false, request: null })}
              onConfirm={handleMarkReceived}
              loading={rowLoadingState[receiveModal.request?.request_id] === 'received'}
            />
          )}
        </div>
      );
    }
    
    return (
      <div className="p-4 text-center text-gray-500">
        <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm font-medium">No jurisdictions found on this order.</p>
        <p className="text-xs mt-1 text-gray-400">
          To enable Mail-Away, go to the Tax Certificate tab and add jurisdiction types in the Current Tax Summary section.
        </p>
        <p className="text-xs mt-2 text-blue-500">
          💡 Select &quot;County&quot;, &quot;City&quot;, &quot;School&quot; etc. from the Jurisdiction Type dropdown for each tax line item.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Header with Summary - Compact */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">Mail-Away Requests</h3>
          <p className="text-xs text-gray-500">
            {_hdr.total} {_hdr.total === 1 ? 'request' : 'requests'} • {_hdr.pending} pending • {_hdr.received} received{_hdr.cancelled > 0 ? ` • ${_hdr.cancelled} cancelled` : ''}
          </p>
        </div>
        {/* Feb 2026 — Order Summary used to host a "Send Mail-Away
            for All" button here as a second entry point to the
            multi-jurisdiction Mail-Away modal. Per the user UX
            review, the Fulfillment tab's "Mail-Away (All
            Jurisdictions)" action is now the SINGLE entry point
            for creating Mail-Away requests. Order Summary stays
            review-only — its rows still expose Confirm Sent /
            Cancel / Preview PDF per-request actions. The
            previously-wired `sendForAllOpen` modal + its loader
            are retained below in case the per-row "Resend" /
            cycle-restart path reuses them, but the top-level
            entry button is intentionally removed. */}
      </div>
      
      {/* Jurisdiction List - Directly below header */}
      {/* Only show jurisdictions with active requests */}
      <div className="space-y-2">
        {jurisdictionList
          .filter(({ request }) => request !== null && request !== undefined)
          .map(({ jurisdiction, request }) => (
          <JurisdictionRow
            key={jurisdiction.id}
            jurisdiction={jurisdiction}
            request={request}
            isProcessor={isProcessor}
            isTeamLeaderOrAdmin={isTeamLeaderOrAdmin}
            hasPermission={hasPermission}
            orderId={orderId}
            onSend={() => {}} // Removed - auto-send handles emails
            onResend={(r) => setResendModal({ open: true, request: r })}
            onMarkSent={(r) => handleMarkSent(r)}
            onMarkReceived={(r) => setReceiveModal({ open: true, request: r })}
            onCancel={(r) => setCancelModal({ open: true, request: r })}
            rowLoadingAction={rowLoadingState[request.request_id]}
          />
        ))}
      </div>
      
      {/* Total MLC Fee Summary - Show if multiple jurisdictions have fees */}
      {extraRows}
      {(() => {
        // MLC-FEE-PER-PARCEL (Jun 2026) — Sum per-REQUEST totals so the
        // banner always matches the PDF fee table and billing:
        //   1. request.tax_office_fee — authoritative stored TOTAL
        //      (base × parcels for current-tax, flat for delinquent)
        //   2. collector_defaults_snapshot.mlc_fee — per-parcel base;
        //      × parcels for current-tax, flat for delinquent
        //   3. jurisdiction.mlc_charges — same semantics (last resort)
        // Prior code treated the snapshot as per-parcel but preferred it
        // over tax_office_fee, inflating current-tax totals ($25 config ×
        // 2 parcels showed $50 while the PDF said $25).
        const getResolvedRequestTotal = (request, jurisdiction) => {
          if (request?.tax_office_fee != null && !isNaN(parseFloat(request.tax_office_fee))) {
            return parseFloat(request.tax_office_fee);
          }
          const parcelCount = (request?.parcel_numbers?.length) || 1;
          const isCurrentKind = (request?.request_kind || 'current') === 'current';
          const snap = request?.collector_defaults_snapshot;
          if (snap && typeof snap === 'object') {
            const byJur = snap[jurisdiction?.id] || snap[jurisdiction?.jurisdiction_id];
            const flatFee = (byJur && byJur.mlc_fee != null) ? byJur.mlc_fee
                          : (snap.mlc_fee != null ? snap.mlc_fee : null);
            if (flatFee != null && !isNaN(parseFloat(flatFee))) {
              return isCurrentKind ? parseFloat(flatFee) * parcelCount : parseFloat(flatFee);
            }
          }
          if (jurisdiction?.mlc_charges != null) {
            const base = parseFloat(jurisdiction.mlc_charges);
            return isCurrentKind ? base * parcelCount : base;
          }
          return null;
        };
        // Feb 2026 (P0) — Exclude cancelled / failed / pre-send rows from
        // the Total MLC Fees roll-up. Prior to this filter a cancelled or
        // send-failed request still rolled into the totalizer, inflating
        // the order's billable MLC charges. The status set below mirrors
        // every Mail-Away terminal-non-billable state the backend writes.
        const _EXCLUDED_STATUSES_FOR_TOTAL = new Set([
          'cancelled',
          'failed',
          'send_failed',
          'dispatch_failed',
          'not_sent',
          'pending_send',
          'draft',
        ]);
        const rows = jurisdictionList
          .filter(({ request }) => request !== null && request !== undefined)
          .filter(({ request }) => {
            const _s = String(request?.status || '').toLowerCase().trim();
            return !_EXCLUDED_STATUSES_FOR_TOTAL.has(_s);
          })
          .map(({ jurisdiction, request }) => ({
            jurisdiction,
            request,
            requestTotal: getResolvedRequestTotal(request, jurisdiction),
          }))
          .filter((r) => r.requestTotal != null && r.requestTotal > 0);
        const extraFee = extraSummary?.fee || 0;
        const totalFee = rows.reduce((sum, { requestTotal }) => sum + requestTotal, 0) + extraFee;
        const uniqueJurisdictionCount = new Set(
          rows.map(({ jurisdiction }) => (jurisdiction.name || jurisdiction.id || '').toLowerCase().trim())
        ).size + (extraSummary?.feeCount || 0);

        if (rows.length >= 1 || extraFee > 0) {
          return (
            <div
              className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg"
              data-testid="mailaway-total-mlc-fees"
            >
              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">Total MLC Fees</span>
                <span className="text-xs text-emerald-600">({uniqueJurisdictionCount} {uniqueJurisdictionCount === 1 ? 'jurisdiction' : 'jurisdictions'}{extraFee > 0 ? ' · incl. MLS' : ''})</span>
              </div>
              <span className="text-sm font-bold text-emerald-800" data-testid="mailaway-total-mlc-fees-amount">${totalFee.toFixed(2)}</span>
            </div>
          );
        }
        return null;
      })()}
      
      {/* Processor Notice */}
      {isProcessor && mailAwayData.total_requests > 0 && (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
          <AlertCircle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-yellow-800">
            You cannot resend or modify Mail-Away requests. Contact your Team Leader for corrections.
          </p>
        </div>
      )}
      
      {/* Modals */}
      {/* REMOVED: SendMailAwayModal - auto-send handles all email sending now */}
      
      {resendModal.open && (
        <ResendModal
          request={resendModal.request}
          onConfirm={handleResend}
          onClose={() => setResendModal({ open: false, request: null })}
          loading={rowLoadingState[resendModal.request?.request_id] === 'resend'}
        />
      )}
      
      {receiveModal.open && (
        <MarkReceivedModal
          request={receiveModal.request}
          onConfirm={handleMarkReceived}
          onClose={() => setReceiveModal({ open: false, request: null })}
          loading={rowLoadingState[receiveModal.request?.request_id] === 'received'}
        />
      )}
      
      {cancelModal.open && cancelModal.request && (
        <CancelMailAwayModal
          request={cancelModal.request}
          onConfirm={handleCancel}
          onClose={() => setCancelModal({ open: false, request: null })}
          loading={rowLoadingState[cancelModal.request?.request_id] === 'cancel'}
        />
      )}

      {cycleAckModal.open && cycleAckModal.jurisdiction && (
        <CycleAckModal
          jurisdiction={cycleAckModal.jurisdiction}
          notes={cycleAckModal.notes}
          pendingCycles={cycleAckModal.pendingCycles}
          loading={rowLoadingState[cycleAckModal.jurisdiction.id] === 'send'}
          onConfirm={(opts) =>
            handleSend(
              cycleAckModal.jurisdiction,
              cycleAckModal.notes,
              opts?.resume ? false : true,   // legacy ack only when NOT resuming
              !!opts?.resume,                // resume flag from green modal
            )
          }
          onClose={() =>
            setCycleAckModal({ open: false, jurisdiction: null, notes: null, pendingCycles: [] })
          }
        />
      )}

      {/* Feb 2026 — Option C: order-level batch Mail-Away modal.
          Pre-selects only the unsent jurisdictions and surfaces the
          PA Delinquent-Grouping banner when applicable. */}
      {sendForAllOpen && orderForBatch && (
        <MailAwayModal
          isOpen={sendForAllOpen}
          orderData={orderForBatch}
          certificateData={certForBatch}
          targetJurisdiction={null}
          preselectUnsentOnly={true}
          onClose={() => {
            setSendForAllOpen(false);
            setOrderForBatch(null);
            setCertForBatch(null);
          }}
          onSubmit={async (formData) => {
            // Reuse the existing /mailaway/send route. formData carries
            // jurisdiction_shipping + (optionally) delinquent_groups from
            // the banner. We pass jurisdiction_ids from the shipping list.
            try {
              const ids = (formData?.jurisdiction_shipping || [])
                .map((s) => s?.jurisdiction_id)
                .filter(Boolean);
              const body = {
                jurisdiction_ids: ids,
                notes: null,
                tax_office_fee: formData?.tax_office_fee || 0,
                jurisdiction_shipping: formData?.jurisdiction_shipping || [],
                collector_defaults_snapshot: formData?.collector_defaults_snapshot || null,
              };
              if (Array.isArray(formData?.delinquent_groups) && formData.delinquent_groups.length > 0) {
                body.delinquent_groups = formData.delinquent_groups;
              }
              const res = await fetch(`${API}/api/orders/${orderId}/mailaway/send`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(body),
              });
              if (!res.ok) {
                const err = await res.text();
                throw new Error(err || `HTTP ${res.status}`);
              }
              await fetchMailAwayData?.();
              if (onUpdate) onUpdate();
              setSendForAllOpen(false);
              setOrderForBatch(null);
              setCertForBatch(null);
            } catch (e) {
              console.error('[mailaway-batch] submit failed', e);
              throw e;  // let the modal surface the error
            }
          }}
        />
      )}
    </div>
  );
};

export default MultiJurisdictionMailAway;
